import { useQuery } from '@tanstack/react-query';

import { Workspaces } from '@air/api';
import { ViewTypeName } from '@air/api/types';

import { getConfigViewOptionsKey } from '~/constants/react-query-keys';
import { useCanViewPrivateMetrics } from '~/hooks/useCanViewPrivateMetrics';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { filterOutInternalAnalyticsFields } from '~/swr-hooks/gallery/viewFields/filterOutInternalAnalyticsFields';
import { filterOutIPTCFields } from '~/swr-hooks/gallery/viewFields/filterOutIPTCFields';
import { useCanViewPrivateAddOn } from '~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn';

export const useGetPrivateConfigViewOptions = ({ viewType }: { viewType: ViewTypeName | undefined }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const { canViewAddOn: canSeePrivateIPTCFields } = useCanViewPrivateAddOn({ key: 'metadata' });
  const { canViewMetrics, hasResolvedMetricsAccess } = useCanViewPrivateMetrics();
  const isTableView = viewType === ViewTypeName.table;
  const canSeeMetricFields = isTableView && canViewMetrics;

  return useQuery({
    queryKey: getConfigViewOptionsKey({
      workspaceId: currentWorkspace?.id,
      viewType,
      canSeeIPTCFields: canSeePrivateIPTCFields,
    }),

    queryFn: async () => {
      if (!workspaceId) {
        throw new Error('No workspace id');
      }

      if (!viewType) {
        throw new Error('No view type');
      }

      const response = await Workspaces.listViewFields({ name: viewType, workspaceId });

      return canSeePrivateIPTCFields ? response : filterOutIPTCFields(response);
    },
    // A table query that resolves while access is still loading strips Views and Downloads, and library pages persist that list.
    enabled: !!viewType && !!workspaceId && (!isTableView || hasResolvedMetricsAccess),
    select: (response) => (canSeeMetricFields ? response : filterOutInternalAnalyticsFields(response)),
  });
};
